import { ActorBasicData } from "src/contexts/_shared/domain/ActorBasicData";
import { StorageFileWithContent } from "src/contexts/_shared/domain/StorageFileWithContent";
import { delay } from "src/contexts/_shared/utils";
import { AcceptanceCheckbox } from "../../domain/AcceptanceCheckbox";
import { Actor } from "../../domain/Actor";
import { ActorId } from "../../domain/ActorId";
import { DocumentationArea } from "../../domain/DocumentationArea";
import { DocumentationAreaId } from "../../domain/DocumentationAreaId";
import {
  DocumentationAreaRepository,
  ExchangeSlugByDocumentationAreaIdAndActorIdResponse,
} from "../../domain/DocumentationAreaRepository";
import { IdNumberTypes } from "../../domain/IdNumberTypes";
import { UserLink } from "../../domain/UserLink";
import { Customization } from "../../domain/Customization";

import { LinkTypes } from "src/contexts/_shared/domain/LinkType";
import { IdentityVerificationDocumentSides } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentSides";
import { OpenAdminBanner } from "../../domain/OpenAdminBanner";
import { OpenAdminBannerStatus } from "../../domain/OpenAdminBannerStatus";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { OrderId } from "../../domain/OrderId";
import { OrderStatus } from "../../domain/OrderStatus";
import { OrderModel } from "../../domain/OrderModel";
import { Month } from "../../domain/Model";
import { OrderPayslip } from "../../domain/OrderPayslip";
import { SUPPORTED_MODEL_TYPES } from "src/contexts/_shared/domain/SupportedModelTypes";
import { DocumentationAreaTypes } from "src/contexts/documentationArea/domain/DocumentationAreaTypes";
import { ActorIdentity } from "src/contexts/documentationArea/domain/ActorIdentity";
import { ActorIdentityRepresentative } from "src/contexts/documentationArea/domain/ActorIdentityRepresentative";
import { ActorIdentityRepresentedEntity } from "src/contexts/documentationArea/domain/ActorIdentityRepresentedEntity";
import { UserLinkId } from "src/contexts/documentationArea/domain/UserLinkId";
import { DocumentationAreaActorOrderRetrievalMethod } from "src/contexts/documentationArea/domain/DocumentationAreaActorOrderRetrievalMethod";
import { DocumentationAreaActorOrderRetrievalMethodNames } from "../../domain/DocumentationAreaActorOrderRetrievalMethodNames";
import { DocumentationAreaActorBasicData } from "src/contexts/documentationArea/domain/DocumentationAreaActorBasicData";
import { ExtractionReason } from "../../domain/ExtractionReason";
import { ExtractionStatus } from "../../domain/ExtractionStatus";
import { colors } from "@permisso/permisso-components";

export class StaticDocumentationAreaRepository implements DocumentationAreaRepository {
  constructor() {}
  private hasCalledUpdateBasicData = false;

  public async getConsents(_: string, __: string) {
    await delay(2000);
    return [
      {
        text: "Acepto la Política de Privacidad, así como los Términos y Condiciones, y concedo permiso para mantener un historial de mis transacciones.",
      },
      {
        text: "Estoy de acuerdo con la Política de Privacidad, y los Términos y Condiciones que rigen la prestación de servicios de Nombre del Cliente.",
      },
      {
        text: "Entiendo que los documentos subidos se enviarán automáticamente a {{companyName}}.",
      },
    ];
  }

  public async getCustomization(_: string, __: LinkTypes): Promise<Customization> {
    const customization: Customization = {
      companyName: "ING Direct",
      companyLogo:
        "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEh8QrRVVa_9L2W2MoY_HhQVxMcmfBKG_3iL6LJpn3v3ZS5u_GG3HzAi3-L0KpXs4lO3_PNfqL0eXE33kYbJkeeAdpN1q8VNDTkp1Akwh_6wC3KEYc2J_2SXDI7Jsto769_NgvBVFBVkIjj_/s1600/ing.jpg",
      primaryColor: colors.defaultYellow,
      linkColor: colors.grey700,
      visitedLinkColor: colors.grey700,
      blackColor: colors.grey800,
      errorColor: colors.darkRed,
      successColor: colors.darkGreen,
      tabBarColor: colors.defaultYellow,
      fontFamily: "'Roboto', sans-serif",
      fontFamilyHeadings: "'Roboto', sans-serif",
      fontSize: 16,
      fontLinks: ["https://fonts.googleapis.com/css?family=Roboto:400,500,700"],
      removeHeader: false,
      removeCustomerLogo: true,
      removePoweredBy: true,
    };

    return customization;
  }

  public async getCustomizationByDocumentationAreaId(
    _: DocumentationAreaId,
  ): Promise<Customization> {
    const customization: Customization = {
      companyName: "ING Direct",
      companyLogo:
        "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEh8QrRVVa_9L2W2MoY_HhQVxMcmfBKG_3iL6LJpn3v3ZS5u_GG3HzAi3-L0KpXs4lO3_PNfqL0eXE33kYbJkeeAdpN1q8VNDTkp1Akwh_6wC3KEYc2J_2SXDI7Jsto769_NgvBVFBVkIjj_/s1600/ing.jpg",
      primaryColor: colors.defaultRed,
      linkColor: colors.grey700,
      visitedLinkColor: colors.grey700,
      blackColor: colors.grey800,
      errorColor: colors.darkRed,
      successColor: colors.darkGreen,
      tabBarColor: colors.defaultRed,
      fontFamily: "'Roboto', sans-serif",
      fontFamilyHeadings: "'Roboto', sans-serif",
      fontSize: 16,
      fontLinks: ["https://fonts.googleapis.com/css?family=Roboto:400,500,700"],
      removeHeader: false,
      removeCustomerLogo: true,
      removePoweredBy: true,
    };

    return customization;
  }

  public async getDocumentationArea(
    _: string,
    __: DocumentationAreaId,
    actorId: ActorId,
    ___: string | null,
  ): Promise<DocumentationArea> {
    await delay(2000);
    const actorName = this.hasCalledUpdateBasicData ? "Miquel" : null; // simulates basic data being complete after basic data update

    const createActor = (id: string) =>
      new Actor({
        id,
        identity: new ActorIdentity(
          new ActorIdentityRepresentative({
            name: actorName,
            firstSurname: "Botticelli",
            secondSurname: null,
            email: "miquelangeloboticelli@gmail.com",
            idNumbers: [
              {
                number: "123456789A",
                type: IdNumberTypes.ID_CARD,
                country: "es",
                verified: true,
              },
            ],
          }),
          new ActorIdentityRepresentedEntity({
            legalName: "Example",
            individualRelationType: "Example relation",
            idNumbers: [
              {
                number: "A123456789",
                type: IdNumberTypes.ID_CARD,
                country: "es",
                verified: true,
              },
            ],
          }),
        ),
        signedAcceptanceIds: ["A", "B"],
        notifications: [],
        orders: [
          new OrderPayslip({
            id: "caf85613-9c69-4382-89b0-54d034d8e64b",
            request: {
              month: Month.JULY,
              year: 2025,
            },
            responses: [],
            retrievalMethods: [
              new DocumentationAreaActorOrderRetrievalMethod({
                name: DocumentationAreaActorOrderRetrievalMethodNames.MANUAL_UPLOAD,
                config: null,
              }),
            ],
            expired: false,
            status: OrderStatus.IDLE,
          }),
          new OrderPayslip({
            id: "d233ceb8-0e27-4ae9-95d9-558a144b12d7",
            request: {
              month: Month.JUNE,
              year: 2025,
            },
            responses: [],
            retrievalMethods: [
              new DocumentationAreaActorOrderRetrievalMethod({
                name: DocumentationAreaActorOrderRetrievalMethodNames.MANUAL_UPLOAD,
                config: null,
              }),
            ],
            expired: false,
            status: OrderStatus.IDLE,
          }),
          new OrderModel({
            id: "7436e2c8-98d0-46bb-88a2-e0251ca0fdb0",
            request: {
              type: SUPPORTED_MODEL_TYPES.AEAT_100_RENTA,
              year: 2024,
            },
            responses: [],
            retrievalMethods: [
              new DocumentationAreaActorOrderRetrievalMethod({
                name: DocumentationAreaActorOrderRetrievalMethodNames.MANUAL_UPLOAD,
                config: null,
              }),
            ],
            status: OrderStatus.IDLE,
            extractionDetails: {
              status: ExtractionStatus.FAILED,
              reason: ExtractionReason.DOCUMENT_NOT_FOUND,
            },
          }),
          new OrderModel({
            id: "b7e5c1a4-2f63-4d90-8a17-6c4e9b2d5f80",
            request: {
              type: SUPPORTED_MODEL_TYPES.SEG_SOCIAL_VIDA_LABORAL,
              year: 2024,
            },
            responses: [],
            retrievalMethods: [
              new DocumentationAreaActorOrderRetrievalMethod({
                name: DocumentationAreaActorOrderRetrievalMethodNames.AUTOMATIC_EXTRACTION,
                config: null,
              }),
            ],
            status: OrderStatus.IDLE,
            extractionDetails: {
              status: ExtractionStatus.PENDING,
              reason: ExtractionReason.PENDING_ON_ASYNC_EXTRACTION,
            },
          }),
        ],
        openAdminBanner: new OpenAdminBanner({
          status: OpenAdminBannerStatus.INITIAL,
          title: "Complete your documentation",
          subtitle: "Start the process to verify your identity",
          buttonText: "Start",
          sessionLink: "some-widget-link",
        }),
        prefilledData: {
          identity: {
            representative: {
              email: {
                value: "test@test.com",
                editable: true,
              },
              name: {
                value: "Name",
                editable: true,
              },
              firstSurname: {
                value: "Surname",
                editable: true,
              },
              secondSurname: {
                value: "SecondSurname",
                editable: true,
              },
              idNumbers: [
                {
                  type: {
                    value: "IDC",
                  },
                  data: {
                    number: { value: "46418958J", editable: false },
                    country: { value: "ES", editable: false },
                  },
                },
              ],
            },
            representedEntity: {
              legalName: {
                value: "Permisso",
                editable: true,
              },
              individualRelationType: {
                value: "apoderado",
                editable: true,
              },
              idNumbers: [
                {
                  type: {
                    value: "IDC",
                  },
                  data: {
                    number: { value: "A46418958", editable: false },
                    country: { value: "ES", editable: false },
                  },
                },
              ],
            },
          },
        },
      });

    const firstActor = createActor("some_id");
    const secondActor = createActor("some_id_2");
    const actors = [firstActor, secondActor];
    const selectedActor = actors.find((actor) => actor.getId() === actorId);
    if (!selectedActor) {
      throw new Error(`Actor not found for actorId: ${actorId}`);
    }

    return new DocumentationArea({
      id: "static",
      type: DocumentationAreaTypes.INDIVIDUAL,
      actor: selectedActor,
      availableActors: actors.map((availableActor) => ({
        id: availableActor.getId(),
        readOnly: availableActor.getId() !== "some_id",
        fullName: `${availableActor.getName() ?? ""} ${availableActor.getFirstSurname() ?? ""}`
          .trim()
          .replace(/\s+/g, " "),
      })),
      templateName: "Autonomo",
      availableTemplates: [
        { id: "actor-template-id-2", name: "Empleado" },
        { id: "actor-template-id-3", name: "Pensionista" },
      ],
      locale: "es",
      actorInvitation: {
        enabled: true,
        reason: null,
      },
    });
  }

  public async getDocumentationAreaIdAndActorIdByMagicLinkSlug(
    _: string,
    __: string,
  ): Promise<ExchangeSlugByDocumentationAreaIdAndActorIdResponse> {
    await delay(2000);
    return {
      documentationAreaId: "some-documentation-area-id" as DocumentationAreaId,
      actorId: "some_id" as ActorId,
    };
  }

  public async getDocumentationAreaIdAndActorIdByUserLinkSlug(
    _: string,
    __: string,
  ): Promise<ExchangeSlugByDocumentationAreaIdAndActorIdResponse> {
    await delay(2000);
    return {
      documentationAreaId: "some-documentation-area-id" as DocumentationAreaId,
      actorId: "some_id" as ActorId,
    };
  }

  public async create(
    _: string,
    __: string,
    ___: {
      name: string;
      firstSurname: string;
      secondSurname?: string;
      idNumber: {
        number: string;
        type: string;
        country: string;
      };
      checkboxes: string[];
      locale: string;
    },
  ) {
    await delay(2000);

    return "some-id";
  }

  public async deliverNotification(_: string, __: DocumentationAreaId, ___: ActorId, ____: string) {
    return;
  }

  public async patchLocale(_: string, __: DocumentationAreaId, ___: ActorId, ____: string) {
    return;
  }

  public async getLocaleConfig(_: string, __: string) {
    return {
      availableLocales: ["es", "en", "ca"],
      resolvedLocale: "es",
    };
  }

  public async patchActorOrder(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: OrderId,
    _____: StorageFileWithContent[],
  ) {
    return;
  }

  public async patchActorIDVOrder(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: OrderId,
    _____?: IdentityVerificationDocumentTypes,
    ______?: string,
    _______?: StorageFileWithContent,
    ________?: StorageFileWithContent,
  ) {
    return;
  }

  private hasSignedAcceptanceCalled = false;

  public async getHasSignedAcceptance(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
  ): Promise<boolean> {
    await delay(2000);

    //Simulates being false first time, and true when refetch is triggered
    if (!this.hasSignedAcceptanceCalled) {
      this.hasSignedAcceptanceCalled = true;
      return false;
    }
    return true;
  }

  public async createSignedAcceptances(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: AcceptanceCheckbox[],
  ) {
    await delay(2000);
    return;
  }

  public async updateBasicData(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: ActorBasicData,
  ) {
    await delay(2000);
    this.hasCalledUpdateBasicData = true;
    return;
  }

  public async getActorBasicData(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
  ): Promise<DocumentationAreaActorBasicData> {
    await delay(1000);
    return {
      isFullfilled: this.hasCalledUpdateBasicData,
      prefilledData: undefined,
      availableTemplates: [
        {
          id: "actor-template-id-1",
          key: "Autonomo",
        },
        {
          id: "actor-template-id-2",
          key: "Empleado",
        },
        {
          id: "actor-template-id-3",
          key: "Pensionista",
        },
      ],
    };
  }

  public async updateActorFromActorTemplateId(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
  ): Promise<void> {
    await delay(500);
  }

  public async createActorInvitation(
    _: string,
    __: DocumentationAreaId,
    ___: { email: string; actorId: ActorId },
  ): Promise<void> {
    await delay(800);
  }

  public async getUserLink(_: string): Promise<UserLink> {
    await delay(2000);
    return {
      id: "e846c707-11a6-43f5-b280-1435668a356f",
      email: "s*******l@permisso.io",
      // email: "",
    };
  }

  public async createOpenAdminSession(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
  ) {
    await delay(2000);
    return {
      id: "some-id",
      widgetLink: "some-widget-link",
    };
  }

  public async createOpenAdminIDVSession(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
  ) {
    await delay(2000);
    return {
      id: "some-id",
      widgetLink: "some-widget-link",
    };
  }

  public async postDiscardIdentityVerificationStagedFile(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
    _____: string,
  ) {
    return;
  }
  public async patchIdentityVerificationDocumentType(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
    _____: IdentityVerificationDocumentTypes,
  ) {
    return;
  }
  public async patchIdentityVerificationCountry(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
    _____: string,
  ) {
    return;
  }
  public async postIdentityVerificationFile(
    _: string,
    __: DocumentationAreaId,
    ___: ActorId,
    ____: string,
    _____: IdentityVerificationDocumentSides,
    ______: StorageFileWithContent,
  ) {
    await delay(1000);
    return;
  }

  public async track(
    _: string,
    __: DocumentationAreaId,
    _______: ActorId,
    ___: string,
    ____?: Record<string, unknown>,
    _____?: Record<string, unknown>,
  ) {
    return;
  }

  public async trackUserLink(
    _: UserLinkId,
    __: string,
    ___?: Record<string, unknown>,
    ____?: Record<string, unknown>,
  ) {
    return;
  }
}
